import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { refreshFeatures } from '../lib/features';
import { ago } from './CalendarFeeds';
import { useSettings } from '../pages/settings/SettingsContext';
import './FeatureSwitches.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Settings -> Features (migration 078): who may use Links, To do, Meetings,
 * call recording and the AI.
 *
 * Gordon, 22 October: "Add visibility settings for Links, To Do, Meetings,
 * Transcription & Summarising and the use of the AI ... If I want to remove
 * their accessibility let me."
 *
 * One card per feature: who it is for (the audience), and two lists of
 * people - "Also allow" for somebody the audience leaves out, "Except" for
 * somebody it lets in. Nothing is saved until Save, so an administrator can
 * pick the audience and the people and look at the result before anybody's
 * screen changes; Undo changes puts the card back as it was.
 *
 * Every default is what Jarvis did before this screen existed - each card
 * says what that was - and every change goes in the Audit Log. Switching
 * something off hides it everywhere and the server refuses it; nothing is
 * deleted. Off is off for administrators too, which is the honest preview
 * of what everybody else now sees; this screen is never behind a switch,
 * so it is always here to turn it back on.
 */

const ROLE_WORD = { admin: 'Admin', staff: 'Staff', contractor: 'Contractor' };
const samePeople = (a, b) => a.length === b.length && a.every((x) => b.some((y) => y.kind === x.kind && y.id === x.id));
const keyOf = (p) => `${p.kind}:${p.id}`;

/** Status words for the card's pill. */
function pillOf(f, audience) {
  if (audience === 'off') return { word: 'Off', cls: 'is-off' };
  if (f.is_default && audience === f.default) return { word: 'As today', cls: 'is-default' };
  return { word: 'Changed', cls: 'is-changed' };
}

/**
 * A list of people as chips, and a search box to add one - a combobox: type
 * to filter, arrows to move, Enter to add, Escape to close. Each chip's ×
 * removes that person; Backspace in an empty box removes the last.
 */
function PeoplePicker({ label, hint, people, chosen, onChange, disabled = false, disabledWhy = null, taken = [] }) {
  const id = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(0);
  const box = useRef(null);
  const input = useRef(null);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const chosenKeys = new Set(chosen.map(keyOf));
    const takenKeys = new Set(taken.map(keyOf));
    return people
      .filter((p) => !chosenKeys.has(keyOf(p)) && !takenKeys.has(keyOf(p)))
      .filter((p) => !needle || `${p.name} ${p.email || ''}`.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [q, people, chosen, taken]);

  useEffect(() => { setAt(0); }, [q, open]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const add = (p) => {
    onChange([...chosen, { kind: p.kind, id: p.id, name: p.name, email: p.email, role: p.role }]);
    setQ('');
    // Closed again after each pick, so the list never sits over the card's
    // own buttons; typing or the down arrow opens it for the next one.
    setOpen(false);
    if (input.current) input.current.focus();
  };
  const remove = (p) => onChange(chosen.filter((x) => keyOf(x) !== keyOf(p)));

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setAt((i) => Math.min(i + 1, Math.max(matches.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      if (open && matches[at]) { e.preventDefault(); add(matches[at]); }
    } else if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); }
    } else if (e.key === 'Backspace' && !q && chosen.length) {
      remove(chosen[chosen.length - 1]);
    }
  };

  const listId = `${id}-list`;
  return (
    <div className={`fsw-people${disabled ? ' is-disabled' : ''}`} ref={box}>
      <label className="fsw-people-label" htmlFor={`${id}-q`}>{label}</label>
      <p className="fsw-people-hint" id={`${id}-hint`}>{disabled && disabledWhy ? disabledWhy : hint}</p>
      <div className="fsw-chipbox" onClick={() => !disabled && input.current && input.current.focus()}>
        {chosen.map((p) => (
          <span key={keyOf(p)} className={`fsw-chip is-${p.role || p.kind}`} data-person={keyOf(p)}>
            <span className="fsw-chip-name">{p.name}</span>
            <span className="fsw-chip-role">{ROLE_WORD[p.role] || (p.kind === 'contractor' ? 'Contractor' : 'Staff')}</span>
            <button type="button" className="fsw-chip-x" onClick={(e) => { e.stopPropagation(); remove(p); }}
                    aria-label={`Remove ${p.name}`} disabled={disabled}>
              <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            </button>
          </span>
        ))}
        <input
          id={`${id}-q`}
          ref={input}
          className="fsw-chip-input"
          type="text"
          role="combobox"
          aria-expanded={open && !disabled && matches.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-describedby={`${id}-hint`}
          aria-activedescendant={open && matches[at] ? `${id}-opt-${at}` : undefined}
          autoComplete="off"
          placeholder={disabled ? '' : chosen.length ? 'Add somebody…' : 'Type a name…'}
          value={q}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
        />
      </div>
      {open && !disabled && (
        <ul className="fsw-options" id={listId} role="listbox" aria-label={label}>
          {matches.length === 0 ? (
            <li className="fsw-option is-none" role="option" aria-selected="false" aria-disabled="true">
              {q ? 'Nobody else by that name' : 'Everybody is already chosen'}
            </li>
          ) : matches.map((p, i) => (
            <li key={keyOf(p)} id={`${id}-opt-${i}`} role="option" aria-selected={i === at}
                className={`fsw-option${i === at ? ' is-at' : ''}`}
                onMouseEnter={() => setAt(i)}
                onMouseDown={(e) => { e.preventDefault(); add(p); }}>
              <span className="fsw-option-name">{p.name}</span>
              <span className="fsw-option-meta">{ROLE_WORD[p.role] || ''}{p.email ? ` · ${p.email}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FeatureCard({ f, people, ready, legacyRecordingOff, onSaved }) {
  const gid = useId();
  const [audience, setAudience] = useState(f.audience);
  const [allow, setAllow] = useState(f.allow || []);
  const [except, setExcept] = useState(f.except || []);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);

  // A fresh answer from the server (after a save, or a reload) resets the card.
  useEffect(() => {
    setAudience(f.audience); setAllow(f.allow || []); setExcept(f.except || []); setProblem(null);
  }, [f]);

  const dirty = audience !== f.audience || !samePeople(allow, f.allow || []) || !samePeople(except, f.except || []);
  const pill = pillOf(dirty ? { ...f, is_default: false } : f, audience);
  const choice = f.choices.find((c) => c.value === audience);
  const everyone = audience === 'everyone';
  // Help (123) is never a contractor's: its exceptions offer staff only.
  const pickable = useMemo(() => (f.contractors === false ? [...people.staff] : [...people.staff, ...people.contractors]), [people, f.contractors]);

  const save = async () => {
    setSaving(true);
    setProblem(null);
    try {
      const body = { audience, allow: everyone ? [] : allow.map(({ kind, id }) => ({ kind, id })), except: except.map(({ kind, id }) => ({ kind, id })) };
      const r = await axios.put(`${API_BASE}/features/${f.key}`, body);
      onSaved(r.data, r.data?.unchanged ? null : `${f.label}: saved. ${f.choices.find((c) => c.value === r.data.audience)?.label || ''}${r.data.allow?.length ? `, also ${r.data.allow.length} ${r.data.allow.length === 1 ? 'person' : 'people'}` : ''}${r.data.except?.length ? `, except ${r.data.except.length}` : ''}.`);
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not save that switch. Try again.');
    } finally {
      setSaving(false);
    }
  };
  const undo = () => { setAudience(f.audience); setAllow(f.allow || []); setExcept(f.except || []); setProblem(null); };
  const toDefault = () => { setAudience(f.default); setAllow([]); setExcept([]); setProblem(null); };

  return (
    <li className={`fsw-card${audience === 'off' ? ' is-off' : ''}${dirty ? ' is-dirty' : ''}`} data-feature={f.key}
        id={`s-feature-${f.key}`}>
      <div className="fsw-head">
        <div className="fsw-head-main">
          <h3 className="fsw-name" id={`${gid}-name`}>{f.label}</h3>
          <span className={`fsw-pill ${pill.cls}`} data-pill={pill.cls}>{pill.word}</span>
        </div>
        <p className="fsw-blurb">{f.blurb}</p>
      </div>

      <fieldset className="fsw-audience" disabled={!ready || saving}>
        <legend className="fsw-legend">Who can use it</legend>
        <div className="fsw-segments" role="radiogroup" aria-labelledby={`${gid}-name`}>
          {f.choices.map((c) => (
            <label key={c.value} className={`fsw-segment${audience === c.value ? ' is-on' : ''}${c.value === 'off' ? ' is-offchoice' : ''}`}>
              <input type="radio" name={`${gid}-aud`} value={c.value} checked={audience === c.value}
                     onChange={() => setAudience(c.value)} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>
        <p className="fsw-hint" aria-live="polite">
          {choice?.hint}
          {audience === f.default && <span className="fsw-today"> This is how Jarvis worked before this switch existed.</span>}
        </p>
        {audience === 'off' && (
          <p className="fsw-warn" role="note">
            {f.people === false
              ? <>Off hides {f.label} from everybody, administrators included. You can always switch it back on here.</>
              : <>Off hides {f.label} from everybody, administrators included, unless you add them under “Also allow”.
                You can always switch it back on here.</>}
          </p>
        )}
        {/* The one switch for recording since 088. Before that migration
            the old "Calls can be recorded" rule can still say off - said
            here, with the migration that folds it in, rather than leaving
            "Everyone" to record nothing without a word. */}
        {f.key === 'recording' && legacyRecordingOff && (
          <p className="fsw-warn" role="note">
            The old “Calls can be recorded” rule is still off on this server, so nobody can record yet.
            Run npm run migrate:088: it moves that rule into this switch, once.
          </p>
        )}
      </fieldset>

      {/* A switch with no exceptions (Licences, 119): on or off for its
          audience, so there is nobody to name. */}
      {f.people !== false && (
      <div className="fsw-exceptions">
        <PeoplePicker
          label="Also allow these people"
          hint="They can use it although the choice above leaves them out."
          disabled={!ready || saving || everyone}
          disabledWhy="Everybody already can use it."
          people={pickable}
          chosen={everyone ? [] : allow}
          taken={except}
          onChange={setAllow}
        />
        <PeoplePicker
          label="Except these people"
          hint="They cannot use it, even though the choice above includes them."
          disabled={!ready || saving || audience === 'off'}
          disabledWhy="Nobody can use it while it is off."
          people={pickable}
          chosen={audience === 'off' ? [] : except}
          taken={allow}
          onChange={setExcept}
        />
      </div>
      )}

      {problem && <p className="fsw-problem" role="alert">{problem}</p>}

      <div className="fsw-foot">
        <span className="fsw-when">
          {f.updated_at
            ? <>Changed {ago(f.updated_at)}{f.updated_by ? ` by ${f.updated_by}` : ''}.</>
            : <>{f.today}</>}
        </span>
        <div className="fsw-actions">
          {!(audience === f.default && !allow.length && !except.length) && (
            <button type="button" className="btn btn-secondary btn-small fsw-reset" onClick={toDefault} disabled={!ready || saving}>
              Back to how it was
            </button>
          )}
          {dirty && (
            <button type="button" className="btn btn-secondary btn-small fsw-undo" onClick={undo} disabled={saving}>Undo changes</button>
          )}
          <button type="button" className="btn btn-primary btn-small fsw-save" onClick={save} disabled={!ready || saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * `only` draws just those features' cards - the in-context "Who can use it"
 * at the top of an area's own page (Recording & AI, Meetings, Links, To do,
 * Documentation) - without the page title or the history; Feature access
 * draws them all. Either way it is the same card and the same /features,
 * so there is one source. Every save tells the Settings shell, so the
 * search's values and the Overview follow at once.
 */
export default function FeatureSwitches({ only = null }) {
  const { reloadFeatures } = useSettings();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/features`);
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the feature switches');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = (feature, said) => {
    setData((d) => (d ? { ...d, features: d.features.map((x) => (x.key === feature.key ? feature : x)) } : d));
    if (said) setFlash(said);
    // My own bar follows at once, and the history underneath.
    refreshFeatures();
    if (typeof reloadFeatures === 'function') reloadFeatures();
    axios.get(`${API_BASE}/features/history?limit=20`).then((r) => {
      setData((d) => (d ? { ...d, history: r.data?.history || d.history } : d));
    }).catch(() => {});
  };

  const people = data?.people || { staff: [], contractors: [] };
  const ready = data?.ready !== false;

  // An answer without the list (a server that is not this version, or a
  // stand-in) draws no cards rather than taking the whole of Settings down.
  const shown = Array.isArray(data?.features) ? data.features.filter((f) => !only || only.includes(f.key)) : [];

  return (
    <div className={`fsw${only ? ' fsw-only' : ''}`}>
      {!only && (
        <p className="fsw-intro">
          Choose who can use each part of Jarvis. Switching one off hides it everywhere and Jarvis refuses it,
          but nothing is deleted - switch it back on and everything is where it was. Every change is recorded
          in the Audit Log.
        </p>
      )}

      <p className="fsw-flash" role="status" aria-live="polite">{flash || ''}</p>

      {error && <p className="fsw-problem" role="alert">{error}</p>}
      {data && !ready && (
        <p className="fsw-notready" role="status">
          Feature switches need a database update first: run <code>npm run migrate:078</code> on the server.
          Until then everything works exactly as before.
        </p>
      )}
      {!data && !error && <p className="fsw-quiet">Loading…</p>}

      {data && !Array.isArray(data.features) && !error && (
        <p className="fsw-problem" role="alert">The server did not answer with the feature switches.</p>
      )}
      {data && (
        <ul className="fsw-list">
          {shown.map((f) => (
            <FeatureCard key={f.key} f={f} people={people} ready={ready}
                         legacyRecordingOff={data.recording_legacy_off === true} onSaved={saved} />
          ))}
        </ul>
      )}

      {data && ready && !only && (
        <details className="fsw-history" id="s-history" open={false}>
          <summary>Recent changes{data.history?.length ? ` (${data.history.length})` : ''}</summary>
          {data.history?.length ? (
            <ol className="fsw-history-list">
              {data.history.map((h) => (
                <li key={h.id} className="fsw-history-row">
                  <span className="fsw-history-said">{h.said}</span>
                  <span className="fsw-history-who">
                    {h.changed_by}{h.on_behalf_by ? ` (via ${h.on_behalf_by})` : ''} · {ago(h.changed_at)}
                  </span>
                </li>
              ))}
            </ol>
          ) : <p className="fsw-quiet">Nothing has been changed yet: every feature is as it always was.</p>}
        </details>
      )}
    </div>
  );
}
